import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './services/auth-service';
import { configureMsalInstance } from './services/api-client';
import { DeliveryWindowsProvider } from './shared/contexts/DeliveryWindowsContext';
import App from './App';
import './styles/root.css';

const IS_DEV = import.meta.env.DEV;
const msalInstance = new PublicClientApplication(msalConfig);

// Set document title based on environment
const ENV_TITLE_PREFIX: Record<string, string> = {
  local: 'Local - ',
  dev: 'Dev - ',
  staging: 'Staging - ',
};
const envPrefix = ENV_TITLE_PREFIX[import.meta.env.VITE_ENV ?? ''] ?? '';
if (envPrefix) document.title = envPrefix + document.title;

function renderApp(): void {
  configureMsalInstance(msalInstance);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <DeliveryWindowsProvider>
            <App />
          </DeliveryWindowsProvider>
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Initialize MSAL and handle redirect promise before rendering
msalInstance
  .initialize()
  .then(async () => {
    // Handle redirect promise immediately — this must be called before React renders
    // to catch the redirect response from Azure AD
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
        if (IS_DEV) console.log('[main] Redirect handled, account:', response.account.username);
      } else if (IS_DEV && window.location.pathname === '/auth/callback') {
        console.warn('[main] On callback route but no redirect response — check redirect URI config');
      }
    } catch (error) {
      console.error('[main] Error handling redirect promise:', error);
    }

    renderApp();
  })
  .catch((error) => {
    console.error('MSAL initialization error:', error);
    renderApp();
  });
